import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { type TransactionHash, TOKEN_IDS } from "@/types";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { prepareFormTokenAmountDecimals } from "@/utils/token";

/**
 * Prepares a lock transaction
 */
export const prepareMintAndLockTransaction = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account || !walletClient.chain?.id) {
        throw new Error("Wallet account or chain ID is not available");
    }

    const amountBigInt = prepareFormTokenAmountDecimals(amount, TOKEN_IDS.USDC);
    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "mintAndLock",
            args: [walletClient.account.address, amountBigInt, unwindingEpochs],
        }),
    };
};

/**
 * Executes a lock transaction
 */
export const gatewayMintAndLockTransaction = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareMintAndLockTransaction(amount, unwindingEpochs, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};

/**
 * Estimates gas for a lock transaction
 */
export const estimateMintAndLockTransactionGas = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareMintAndLockTransaction(amount, unwindingEpochs, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};
